import { c as _c } from "react/compiler-runtime";
/* eslint-disable react/jsx-props-no-spreading */
import classnames from 'classnames';
import { isUndefined } from 'lodash';
import { useRef, useEffect, useState, type JSX } from 'react';
import * as React from 'react';
import { useToggle } from 'react-use';
import styled, { css } from 'styled-components';
import { useDebouncedCallback } from '../../hooks/debounced-callback';
import { generateTestId } from '../../lib/test-helpers';
import { fontFamilies } from '../../theme';
import { colors } from '../../theme/colors';
import type { Size } from '../date-picker-input/types';
import { IconButton } from '../icon-button';
import { IconDate, IconSearch, IconSearch2 } from '../icons';
import { Padding } from '../padding';
import { Spinner } from '../spinner';
import { Text } from '../text';
type CallbackRef = (node: HTMLElement) => void;
export interface InputProps {
  className?: string;
  debounce?: number;
  defaultValue?: string;
  disabled?: boolean;
  withAiSuggestion?: boolean;
  fieldId?: string;
  height?: number;
  width?: string;
  innerRef?: CallbackRef | React.RefObject<HTMLElement | null> | React.RefObject<HTMLInputElement | null> | React.RefObject<HTMLSelectElement | null> | React.RefObject<HTMLTextAreaElement | null>;
  invisible?: boolean;
  isInvalid?: boolean;
  maxLength?: number;
  name?: string;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => unknown;
  onKeyDown?: (e: React.KeyboardEvent) => unknown;
  onChange?: ChangeHandler;
  onFocus?: (e?: React.FocusEvent<HTMLTextAreaElement> | React.FocusEvent<HTMLInputElement>) => unknown;
  onBlur?: (value?: string) => unknown;
  onInput?: (e: React.FormEvent<HTMLInputElement>) => unknown;
  onEditableToggle?: (isDisabled: boolean) => unknown;
  placeholder?: string;
  placeholderColor?: string;
  isRequired?: boolean;
  testId?: string;
  type: InputType;
  value?: string | number | null;
  autoComplete?: 'on' | 'off' | boolean;
  appendText?: string; // eslint-disable-line react/no-unused-prop-types,
  prependText?: string;
  assistiveText?: string;
  style?: React.CSSProperties;
  size?: Size;
  ariaLabel?: string;
  autoFocus?: boolean;
}
type ChangeHandler = (value: string) => void;
type InputType = 'text' | 'textarea' | 'number' | 'email' | 'tel' | 'select' | 'date' | 'time' | 'search' | 'search-new' | 'react-select' | 'password' | 'editable';
function getIcon(type: InputType): React.ReactNode {
  if (type === 'date') return <IconDate />;
  return null;
}
function InputControl(props) {
  const $ = _c(83);
  const {
    className,
    debounce: t0,
    defaultValue,
    disabled,
    fieldId,
    height,
    width,
    invisible,
    innerRef,
    isInvalid,
    maxLength,
    name,
    onClick,
    onKeyDown,
    onChange,
    onFocus,
    onBlur,
    onInput,
    placeholder,
    type,
    testId,
    isRequired,
    value,
    autoComplete: t1,
    assistiveText: t2,
    style,
    size,
    withAiSuggestion,
    ariaLabel: t3,
    autoFocus,
    placeholderColor
  } = props;
  const debounce = t0 === undefined ? 0 : t0;
  const autoComplete = t1 === undefined ? true : t1;
  const assistiveText = t2 === undefined ? null : t2;
  const ariaLabel = t3 === undefined ? placeholder : t3;
  const debouncedOnChange = useDebouncedCallback(onChange, debounce);
  let t4;
  if ($[0] !== onChange) {
    t4 = isUndefined(onChange);
    $[0] = onChange;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const isReadOnly = t4;
  const normalizedValue = typeof value === "string" || typeof value === "number" ? value : undefined;
  let t5;
  if ($[2] !== debounce || $[3] !== debouncedOnChange || $[4] !== onChange) {
    t5 = function handleChange(e) {
      const inputValue = e.target.value;
      if (debounce === 0) {
        onChange?.(inputValue);
        return;
      }
      if (debouncedOnChange) {
        debouncedOnChange(inputValue);
      }
    };
    $[2] = debounce;
    $[3] = debouncedOnChange;
    $[4] = onChange;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleChange = t5;
  let t6;
  if ($[6] !== fieldId || $[7] !== testId) {
    t6 = function getTestId() {
      return testId ?? generateTestId(fieldId, "input");
    };
    $[6] = fieldId;
    $[7] = testId;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const getTestId = t6;
  let t7;
  if ($[9] !== className || $[10] !== invisible || $[11] !== isInvalid) {
    t7 = classnames({
      control: true,
      isInvalid,
      invisible
    }, className);
    $[9] = className;
    $[10] = invisible;
    $[11] = isInvalid;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const classNames = t7;
  if (type === "textarea") {
    let t8;
    if ($[13] !== getTestId) {
      t8 = getTestId();
      $[13] = getTestId;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    const t9 = innerRef as React.RefObject<HTMLTextAreaElement>;
    const t10 = disabled || isReadOnly;
    let t11;
    if ($[15] !== onBlur) {
      t11 = e_0 => {
        if (onBlur) {
          onBlur(e_0.target.value);
        }
      };
      $[15] = onBlur;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] !== ariaLabel || $[18] !== autoFocus || $[19] !== classNames || $[20] !== defaultValue || $[21] !== fieldId || $[22] !== handleChange || $[23] !== height || $[24] !== isRequired || $[25] !== maxLength || $[26] !== name || $[27] !== normalizedValue || $[28] !== onFocus || $[29] !== onKeyDown || $[30] !== placeholder || $[31] !== style || $[32] !== t10 || $[33] !== t11 || $[34] !== t8 || $[35] !== t9) {
      t12 = <StyledTextArea id={fieldId} data-testid={t8} name={name} ref={t9} className={classNames} placeholder={placeholder} disabled={t10} defaultValue={defaultValue} value={normalizedValue} minHeight={height} maxLength={maxLength} required={isRequired} onKeyDown={onKeyDown} onChange={handleChange} onFocus={onFocus} onBlur={t11} style={style} aria-label={ariaLabel} autoFocus={autoFocus} />;
      $[17] = ariaLabel;
      $[18] = autoFocus;
      $[19] = classNames;
      $[20] = defaultValue;
      $[21] = fieldId;
      $[22] = handleChange;
      $[23] = height;
      $[24] = isRequired;
      $[25] = maxLength;
      $[26] = name;
      $[27] = normalizedValue;
      $[28] = onFocus;
      $[29] = onKeyDown;
      $[30] = placeholder;
      $[31] = style;
      $[32] = t10;
      $[33] = t11;
      $[34] = t8;
      $[35] = t9;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    return t12;
  }
  const styledInputHeight = height || (size === "small" ? 30 : 40);
  let t8;
  if ($[37] !== getTestId) {
    t8 = getTestId();
    $[37] = getTestId;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  const t9 = innerRef as React.RefObject<HTMLInputElement>;
  const t10 = type === "date" ? "text" : type;
  const t11 = disabled || isReadOnly;
  const t12 = width || "100%";
  let t13;
  if ($[39] !== style || $[40] !== styledInputHeight || $[41] !== t12) {
    t13 = {
      width: t12,
      height: styledInputHeight,
      ...style
    };
    $[39] = style;
    $[40] = styledInputHeight;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== onBlur) {
    t14 = e_1 => {
      if (onBlur) {
        onBlur(e_1.target.value);
      }
    };
    $[43] = onBlur;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== onInput) {
    t15 = e_2 => onInput?.(e_2);
    $[45] = onInput;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  const t16 = autoComplete === true || autoComplete === "on" ? "on" : "off";
  const t17 = type === "number" ? _temp : undefined;
  let t18;
  if ($[47] !== ariaLabel || $[48] !== autoFocus || $[49] !== classNames || $[50] !== defaultValue || $[51] !== fieldId || $[52] !== handleChange || $[53] !== isReadOnly || $[54] !== isRequired || $[55] !== name || $[56] !== normalizedValue || $[57] !== onClick || $[58] !== onFocus || $[59] !== onKeyDown || $[60] !== placeholder || $[61] !== placeholderColor || $[62] !== t10 || $[63] !== t11 || $[64] !== t13 || $[65] !== t14 || $[66] !== t15 || $[67] !== t16 || $[68] !== t17 || $[69] !== t8 || $[70] !== t9 || $[71] !== withAiSuggestion) {
    t18 = <StyledInput id={fieldId} withAiSuggestion={withAiSuggestion} data-testid={t8} name={name} ref={t9} className={classNames} type={t10} placeholder={placeholder} placeholderColor={placeholderColor} defaultValue={defaultValue} disabled={t11} value={normalizedValue} readOnly={isReadOnly} required={isRequired} step="any" style={t13} onClick={onClick} onKeyDown={onKeyDown} onChange={handleChange} onFocus={onFocus} onBlur={t14} onInput={t15} autoComplete={t16} onWheel={t17} aria-label={ariaLabel} autoFocus={autoFocus} />;
    $[47] = ariaLabel;
    $[48] = autoFocus;
    $[49] = classNames;
    $[50] = defaultValue;
    $[51] = fieldId;
    $[52] = handleChange;
    $[53] = isReadOnly;
    $[54] = isRequired;
    $[55] = name;
    $[56] = normalizedValue;
    $[57] = onClick;
    $[58] = onFocus;
    $[59] = onKeyDown;
    $[60] = placeholder;
    $[61] = placeholderColor;
    $[62] = t10;
    $[63] = t11;
    $[64] = t13;
    $[65] = t14;
    $[66] = t15;
    $[67] = t16;
    $[68] = t17;
    $[69] = t8;
    $[70] = t9;
    $[71] = withAiSuggestion;
    $[72] = t18;
  } else {
    t18 = $[72];
  }
  let t19;
  if ($[73] !== size || $[74] !== t18 || $[75] !== type) {
    t19 = <StyledInputWrapper type={type} size={size}>{t18}</StyledInputWrapper>;
    $[73] = size;
    $[74] = t18;
    $[75] = type;
    $[76] = t19;
  } else {
    t19 = $[76];
  }
  let t20;
  if ($[77] !== assistiveText || $[78] !== isInvalid) {
    t20 = assistiveText && <Padding top={8} left={4}><Text color={isInvalid ? colors.fire[500] : colors.steel[400]} size="small">{assistiveText}</Text></Padding>;
    $[77] = assistiveText;
    $[78] = isInvalid;
    $[79] = t20;
  } else {
    t20 = $[79];
  }
  let t21;
  if ($[80] !== t19 || $[81] !== t20) {
    t21 = <>{t19}{t20}</>;
    $[80] = t19;
    $[81] = t20;
    $[82] = t21;
  } else {
    t21 = $[82];
  }
  return t21;
}

/**
 * @deprecated Use the `Input` component from the `@newfront-insurance/core-ui/v2` package where possible.
 * Be careful when switching to the v2 version, as `onChange` props are different.
 */
function _temp(e_3) {
  return e_3.currentTarget.blur();
}
export function Input(props: InputProps): JSX.Element {
  const {
    style = {},
    type,
    appendText = null,
    prependText = null,
    size,
    disabled: inputDisabled,
    onEditableToggle,
    withAiSuggestion,
    autoFocus,
    placeholderColor
  } = props;
  const targetRef = useRef<HTMLDivElement>(null);
  const [appendedTextWidth, setAppendedTextWidth] = useState<number | null>(null);
  const [passwordVisibilityType, setPasswordVisibilityType] = useState<InputType>(type);
  const [editableDisabled, toggleEditableDisabled] = useToggle(true);
  const disabled = type === 'editable' ? editableDisabled || inputDisabled : inputDisabled;
  useEffect(() => {
    if (targetRef.current) {
      setAppendedTextWidth(targetRef.current.getBoundingClientRect().width);
    }
  }, [appendText, prependText]);
  const icon = getIcon(type);
  const styles = type === 'number' && appendText ? {
    ...style,
    paddingRight: `${appendedTextWidth ?? 0 + 8}px`
  } : style;

  // if the type is editable or password, we need to make the padding right 40px to account for the icon
  if (type === 'editable' || type === 'password') {
    styles.paddingRight = '40px';
  }
  if (prependText) {
    styles.paddingLeft = `${appendedTextWidth ? appendedTextWidth + 8 : 0 + 8}px`;
  }
  return <StyledInputWrapper type={type} size={size ?? 'regular'}>
      {/* eslint-disable-next-line */}
      {type === 'search' && <div className="input-search">
          <IconSearch />
        </div>}
      {type === 'search-new' && <div className="input-search-new">
          <IconSearch2 color={colors.steel[400]} size={16} />
        </div>}
      {prependText ? <StyledPrependedText ref={targetRef}>
          <Text color="disabled">{prependText}</Text>
        </StyledPrependedText> : undefined}
      <InputControl {...props} disabled={disabled} type={passwordVisibilityType} style={styles} withAiSuggestion={withAiSuggestion} ariaLabel={props.ariaLabel ?? props.placeholder} autoFocus={autoFocus} placeholderColor={placeholderColor} />
      {icon && <div className="input-icon">{icon}</div>}
      {appendText ? <StyledAppendedText ref={targetRef}>
          <Text color="disabled">{appendText}</Text>
        </StyledAppendedText> : null}
      {type === 'password' && <EndAdornment>
          <IconButton type={passwordVisibilityType === 'password' ? 'showPassword' : 'hidePassword'} onClick={() => passwordVisibilityType === 'password' ? setPasswordVisibilityType('text') : setPasswordVisibilityType('password')} ariaLabel={passwordVisibilityType === 'password' ? 'Show password' : 'Hide password'} title={passwordVisibilityType === 'password' ? 'Show password' : 'Hide password'} />
        </EndAdornment>}
      {type === 'editable' && <EndAdornment>
          <IconButton type={editableDisabled ? 'editPencil' : 'regenerate'} onClick={() => {
        toggleEditableDisabled();
        onEditableToggle?.(!editableDisabled);
      }} ariaLabel="Edit" title="Edit" />
        </EndAdornment>}
    </StyledInputWrapper>;
}
export const LoadingTextInput = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <StyledLoadingTextInput><Spinner color={colors.steel[300]} /></StyledLoadingTextInput>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const StyledInput = styled.input<{
  type: InputType;
  withAiSuggestion?: boolean;
  placeholderColor?: string;
}>`
  input[type='search']::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }
  input[type='search-new']::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }

  ${({
  withAiSuggestion
}) => withAiSuggestion && `
  border-color: ${colors.purple[500]} !important;
  background: ${colors.purple[100]};
`}

  &::placeholder {
    font-weight: 300;
    color: ${({
  placeholderColor
}) => placeholderColor ?? colors.steel[300]};
    line-height: 21px;
  }
`;
const StyledInputWrapper = styled.div<{
  type: InputType;
  size?: Size;
  placeholderColor?: string;
}>`
  position: relative;
  flex-grow: 1;

  .control {
    font-family: ${fontFamilies.body};
    font-size: 14px;
    border: 1px solid ${colors.steel[200]};
    line-height: 21px;
    color: ${colors.steel[500]};
    padding: 8px 12px;
    height: ${({
  size
}) => size === 'small' ? '30px' : '40px'};
    box-sizing: border-box;
    width: 100%;
    display: block;
    border-radius: 4px;

    appearance: none;
    ${({
  type
}) => type === 'search' && css`
        padding-left: 32px;
      `};
    ${({
  type
}) => type === 'search-new' && css`
        padding-left: 46px;
      `};
  }
  .input-icon {
    z-index: 1;
    color: ${colors.steel[300]};
    position: absolute;
    top: ${({
  size
}) => size === 'small' ? '5px' : '10px'};
    right: 10px;
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .input-search {
    z-index: 1;
    color: ${colors.steel[500]};
    position: absolute;
    top: 10px;
    left: 10px;
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .input-search-new {
    position: absolute;
    left: 20px;
    top: 14px;
    height: 16px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .control:focus + .input-icon {
    color: ${colors.brand[300]};
  }
  .control.invisible {
    font-size: inherit;
    font-family: inherit;
    line-height: inherit;
    border: none;
    padding: 0;
    height: auto;
  }
  .control.invisible::placeholder {
    font-size: inherit;
    line-height: inherit;
  }
  .control.invisible:focus,
  .control.invisible:active:not(:disabled) {
    border: none;
    box-shadow: none;
  }
  .textarea.control {
    resize: vertical;
  }
  .control:focus {
    outline: none;
  }
  .control:disabled {
    background: ${colors.steel[100]};
    cursor: not-allowed;
  }
  .control:focus,
  .control:active:not(:disabled) {
    border-color: ${colors.brand[400]};
  }
  .control.isInvalid {
    border-color: ${colors.fire[500]};
  }
`;
const StyledLoadingTextInput = styled.div`
  background: #fafafa;
  border: 1px solid ${colors.steel[200]};
  border-radius: 3px;
  cursor: wait;
  height: 38px;
  position: relative;
`;
const StyledTextArea = styled.textarea<{
  minHeight?: number;
}>`
  min-height: ${({
  minHeight
}) => `${minHeight || 40}px`};
  max-width: 100%;
  min-width: 100%;
`;
const StyledAppendedText = styled.div`
  align-items: center;
  background: #fff;
  border-left: 1px solid #dbdbdb;
  bottom: 1px;
  color: ${colors.steel[400]};
  display: flex;
  justify-content: center;
  padding: 0 8px;
  position: absolute;
  right: 1px;
  top: 1px;
  z-index: 1;
  border-start-end-radius: 4px;
  border-end-end-radius: 4px;
  &:after {
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.75));
    bottom: 0;
    content: '';
    left: -31px;
    position: absolute;
    top: 0;
    width: 30px;
    z-index: 0;
  }
`;
const StyledPrependedText = styled.div`
  align-items: center;
  background: ${colors.steel[100]};
  border-right: 1px solid #dbdbdb;
  bottom: 1px;
  color: ${colors.steel[400]};
  display: flex;
  justify-content: center;
  padding-left: 16px;
  padding-right: 16px;
  position: absolute;
  left: 1px;
  top: 1px;
  z-index: 1;
  &:after {
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.75));
    bottom: 0;
    content: '';
    left: -31px;
    position: absolute;
    top: 0;
    width: 30px;
    z-index: 0;
  }
`;
const EndAdornment = styled.div`
  position: absolute;
  right: 12px;
  bottom: 0;
  height: 100%;
  display: flex;
  align-items: center;
`;